import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import HomePage from './pages/HomePage';
import FeaturesPage from './pages/FeaturesPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import DemoPage from './pages/DemoPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import HipaaPage from './pages/HipaaPage';
import PricingPage from './pages/PricingPage';
import IntegrationsPage from './pages/IntegrationsPage';
import QuoteSigningPage from './pages/QuoteSigningPage';

// CRM Imports
import { AuthProvider, useAuth } from './crm/context/AuthContext';
import { CRMLayout } from './crm/components/CRMLayout';
import LoginPage from './crm/pages/LoginPage';
import DashboardPage from './crm/pages/DashboardPage';
import LeadsPage from './crm/pages/LeadsPage';
import LeadDetailPage from './crm/pages/LeadDetailPage';
import ProjectsPage from './crm/pages/ProjectsPage';
import ProjectDetailPage from './crm/pages/ProjectDetailPage';
import TasksPage from './crm/pages/TasksPage';
import ContactsPage from './crm/pages/ContactsPage';
import BookingsPage from './crm/pages/BookingsPage';
import InvoicesPage from './crm/pages/InvoicesPage';
import PaymentSuccessPage from './crm/pages/PaymentSuccessPage';
import SignaturesPage from './crm/pages/SignaturesPage';
import AdminSettingsPage from './crm/pages/AdminSettingsPage';
import DevSettingsPage from './crm/pages/DevSettingsPage';
import SuperAdminPage from './crm/pages/SuperAdminPage';
import LiveChatPage from './crm/pages/LiveChatPage';
// Feature Installer temporarily disabled due to babel plugin compatibility
import ProfilePage from './crm/pages/ProfilePage';
import NPIRegistryPage from './crm/pages/NPIRegistryPage';
import PitchedLeadsPage from './crm/pages/PitchedLeadsPage';
import BillingPage from './crm/pages/BillingPage';
import ClientsPage from './crm/pages/ClientsPage';
import ClientDetailPage from './crm/pages/ClientDetailPage';
import RecurringChargesPage from './crm/pages/RecurringChargesPage';
import AccountingPage from './crm/pages/AccountingPage';
import PartnerPage from './crm/pages/PartnerPage';
import QuoteBuilderPage from './crm/pages/QuoteBuilderPage';
import AdminDashboardPage from './crm/pages/AdminDashboardPage';
import AdminContractsPage from './crm/pages/AdminContractsPage';
import AdminBookingSettingsPage from './crm/pages/AdminBookingSettingsPage';
import AdminSystemCostsPage from './crm/pages/AdminSystemCostsPage';
import EmailStatsPage from './crm/pages/EmailStatsPage';
import { AdminLayout } from './crm/components/AdminLayout';
import PublicBookingPage from './pages/PublicBookingPage';
import ChatWidget from './components/ChatWidget';

// Client Portal Imports
import ClientLoginPage from './portal/ClientLoginPage';
import PortalLayout from './portal/PortalLayout';
import PortalOverviewPage from './portal/PortalOverviewPage';
import PortalInvoicesPage from './portal/PortalInvoicesPage';
import PortalContractsPage from './portal/PortalContractsPage';
import PortalSubscriptionsPage from './portal/PortalSubscriptionsPage';
import PortalPaymentsPage from './portal/PortalPaymentsPage';
import PortalLocationsPage from './portal/PortalLocationsPage';
import PortalProfilePage from './portal/PortalProfilePage';
import PortalPaymentSuccessPage from './portal/PortalPaymentSuccessPage';
import InvoicePrintPage from './pages/InvoicePrintPage';

// PWA Imports
import { PWAProvider, PWAInstallPopup } from './components/PWAInstall';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, superAdminOnly = false }) => {
  const { user, loading, isAdmin, isSuperAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="w-10 h-10 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/crm/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/crm" replace />;
  }

  if (superAdminOnly && !isSuperAdmin) {
    return <Navigate to="/crm" replace />;
  }

  return <CRMLayout>{children}</CRMLayout>;
};

// Admin Protected Route with AdminLayout
const AdminProtectedRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="w-10 h-10 border-4 border-[#84CC16] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/crm/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/crm" replace />;
  }

  return <AdminLayout>{children}</AdminLayout>;
};

// Protected Route without CRMLayout (for popup windows)
const ProtectedRouteNoLayout = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="w-10 h-10 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/crm/login" replace />;
  }

  return children;
};

// Marketing Site Layout
const MarketingLayout = ({ children }) => (
  <div className="App min-h-screen flex flex-col">
    {/* Top Bar - Client Portal Login */}
    <div className="w-full bg-[#0A1628] text-white z-[60] relative" data-testid="marketing-topbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-end h-9">
        <Link to="/portal/login" className="flex items-center gap-1.5 text-xs font-medium text-gray-300 hover:text-white transition-colors" data-testid="topbar-client-portal-link">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
          Client Portal Login
        </Link>
      </div>
    </div>
    <div className="relative">
      <Navbar />
    </div>
    <main className="flex-1">{children}</main>
    <Footer />
    <ChatWidget />
  </div>
);

function AppContent() {
  return (
    <>
      <Routes>
        {/* Marketing Site Routes */}
        <Route path="/" element={<MarketingLayout><HomePage /></MarketingLayout>} />
        <Route path="/features" element={<MarketingLayout><FeaturesPage /></MarketingLayout>} />
        <Route path="/about" element={<MarketingLayout><AboutPage /></MarketingLayout>} />
        <Route path="/contact" element={<MarketingLayout><ContactPage /></MarketingLayout>} />
        <Route path="/demo" element={<MarketingLayout><DemoPage /></MarketingLayout>} />
        <Route path="/privacy" element={<MarketingLayout><PrivacyPage /></MarketingLayout>} />
        <Route path="/terms" element={<MarketingLayout><TermsPage /></MarketingLayout>} />
        <Route path="/hipaa" element={<MarketingLayout><HipaaPage /></MarketingLayout>} />
        <Route path="/pricing" element={<MarketingLayout><PricingPage /></MarketingLayout>} />
        <Route path="/integrations" element={<MarketingLayout><IntegrationsPage /></MarketingLayout>} />
        
        {/* Public Quote Signing (No Auth Required) */}
        <Route path="/sign/:quoteId" element={<QuoteSigningPage />} />
        
        {/* Public Booking Page (No Auth Required) */}
        <Route path="/booking/:slug" element={<PublicBookingPage />} />

        {/* Client Portal Routes */}
        <Route path="/portal/login" element={<ClientLoginPage />} />
        <Route path="/portal/payment-success" element={<PortalPaymentSuccessPage />} />
        <Route path="/portal/invoice/:invoiceId/print" element={<InvoicePrintPage portalMode={true} />} />
        <Route path="/portal" element={<PortalLayout />}>
          <Route index element={<PortalOverviewPage />} />
          <Route path="profile" element={<PortalProfilePage />} />
        </Route>

        {/* CRM Routes */}
        <Route path="/crm/login" element={<LoginPage />} />
        <Route path="/crm" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/crm/leads" element={<ProtectedRoute><LeadsPage /></ProtectedRoute>} />
        <Route path="/crm/leads/:id" element={<ProtectedRoute><LeadDetailPage /></ProtectedRoute>} />
        <Route path="/crm/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
        <Route path="/crm/projects/:id" element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>} />
        <Route path="/crm/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
        <Route path="/crm/contacts" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
        <Route path="/crm/contacts/:id" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
        <Route path="/crm/bookings" element={<ProtectedRoute><BookingsPage /></ProtectedRoute>} />
        <Route path="/crm/invoice/:invoiceId/print" element={<ProtectedRouteNoLayout><InvoicePrintPage /></ProtectedRouteNoLayout>} />
        <Route path="/crm/invoices" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
        <Route path="/crm/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
        <Route path="/crm/recurring" element={<ProtectedRoute adminOnly><RecurringChargesPage /></ProtectedRoute>} />
        <Route path="/crm/signatures" element={<ProtectedRoute><SignaturesPage /></ProtectedRoute>} />
        <Route path="/crm/settings" element={<ProtectedRoute adminOnly><AdminSettingsPage /></ProtectedRoute>} />
        <Route path="/crm/dev-settings" element={<ProtectedRoute superAdminOnly><DevSettingsPage /></ProtectedRoute>} />
        <Route path="/crm/super-admin" element={<ProtectedRoute superAdminOnly><SuperAdminPage /></ProtectedRoute>} />
        {/* Feature Installer route temporarily disabled */}
        <Route path="/crm/live-chat" element={<ProtectedRoute><LiveChatPage /></ProtectedRoute>} />
        <Route path="/crm/live-chat-popup" element={<ProtectedRouteNoLayout><LiveChatPage /></ProtectedRouteNoLayout>} />
        <Route path="/crm/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/crm/npi-registry" element={<ProtectedRoute adminOnly><NPIRegistryPage /></ProtectedRoute>} />
        <Route path="/crm/pitched-leads" element={<ProtectedRoute adminOnly><PitchedLeadsPage /></ProtectedRoute>} />
        <Route path="/crm/billing" element={<ProtectedRoute adminOnly><BillingPage /></ProtectedRoute>} />
        <Route path="/crm/clients" element={<ProtectedRoute adminOnly><ClientsPage /></ProtectedRoute>} />
        <Route path="/crm/clients/:id" element={<ProtectedRoute adminOnly><ClientDetailPage /></ProtectedRoute>} />
        <Route path="/crm/leads/:leadId/quote/:quoteId" element={<ProtectedRoute><QuoteBuilderPage /></ProtectedRoute>} />
        <Route path="/crm/leads/:leadId/quote/new" element={<ProtectedRoute><QuoteBuilderPage /></ProtectedRoute>} />
        
        {/* Admin Dashboard Routes */}
        <Route path="/crm/admin" element={<AdminProtectedRoute><AdminDashboardPage /></AdminProtectedRoute>} />
        <Route path="/crm/admin/contracts" element={<AdminProtectedRoute><AdminContractsPage /></AdminProtectedRoute>} />
        <Route path="/crm/admin/booking-settings" element={<AdminProtectedRoute><AdminBookingSettingsPage /></AdminProtectedRoute>} />
        <Route path="/crm/admin/system-costs" element={<AdminProtectedRoute><AdminSystemCostsPage /></AdminProtectedRoute>} />
        <Route path="/crm/admin/accounting" element={<AdminProtectedRoute><AccountingPage /></AdminProtectedRoute>} />
        <Route path="/crm/admin/email-stats" element={<AdminProtectedRoute><EmailStatsPage /></AdminProtectedRoute>} />
        <Route path="/crm/admin/partners" element={<AdminProtectedRoute><PartnerPage /></AdminProtectedRoute>} />
      </Routes>

      <Toaster 
        position="top-right" 
        richColors 
        closeButton
        toastOptions={{
          style: {
            fontFamily: 'Inter, sans-serif',
          },
        }}
      />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <PWAProvider>
        <AuthProvider>
          <AppContent />
          <PWAInstallPopup />
        </AuthProvider>
      </PWAProvider>
    </BrowserRouter>
  );
}

export default App;
